import type { ReactNode, Ref } from 'react';
import { forwardRef, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import type { ThemeColorsBackground, ThemeColorsMeta } from '@frameio/vapor';
import {
  Avatar,
  Box,
  Collapse,
  CollapseContent,
  Separator,
  Skeleton,
  Text,
  theme,
  TooltipContent,
  useId,
  useTooltip,
  whenFocused,
  whenHovered,
} from '@frameio/vapor';
import { IconInfo } from '@frameio/vapor-icons';
import { useSkeleton } from '@frameio/vapor-migration';
import { withBackgroundTransition, withOpacityTransition } from '../styles';
import type { InviteAccessMemberProps } from '../types';

type InviteAccessMemberRowProps = {
  adornmentAfter?: ReactNode;
  adornmentBefore?: ReactNode;
  avatarBackground?: string;
  avatarSrc?: string;
  buttonLabel?: string;
  collapseContent?: ReactNode;
  enableTooltip?: boolean;
  hasTitlePrefix?: boolean;
  metaContent: ReactNode;
  /**
   * Typed event as any because any type of event could be returned based on
   * interaction
   */
  onClick?: (event?: any) => void;
  onHover?: (isHovered?: boolean) => void;
  queryText?: string;
  showCollapseContent?: boolean;
  secondaryTitle?: ReactNode;
  title: string;
  titleColor?: 'body' | 'secondary' | 'tertiary';
  titleOnHover?: ReactNode;
  tooltipTitle?: string;
  withSeparator?: boolean;
} & Pick<InviteAccessMemberProps, 'backgroundColor'>;

export const InviteAccessMemberRow = ({
  adornmentAfter,
  adornmentBefore,
  avatarBackground,
  avatarSrc,
  buttonLabel,
  collapseContent,
  enableTooltip,
  hasTitlePrefix = false,
  metaContent,
  onClick,
  onHover,
  queryText = '',
  backgroundColor = 'secondary',
  showCollapseContent,
  title = '',
  secondaryTitle,
  titleColor = 'body',
  titleOnHover,
  tooltipTitle,
  withSeparator,
  ...rest
}: InviteAccessMemberRowProps) => {
  const searchWords = useMemo(() => {
    return queryText ? queryText.split(' ') : [];
  }, [queryText]);

  const collapseId = useId();
  const renderAsButton = Boolean(onClick);
  const tooltipResult = useTooltip({ disabled: !enableTooltip });

  const [rowIsHovered, setRowIsHovered] = useState(false);
  const showNonInteractiveHoverState = !onClick && rowIsHovered;

  const { showSkeleton, ref } = useSkeleton({
    id: collapseId,
    lazy: false,
  });

  function handleMouseEnter() {
    if (renderAsButton) {
      setRowIsHovered(true);
    }

    if (onHover) {
      onHover(true);
    }
  }

  function handleMouseLeave() {
    if (renderAsButton) {
      setRowIsHovered(false);
    }

    if (onHover) {
      onHover(false);
    }
  }

  function handleKeyDown(event: { key: string }) {
    if ((event.key === 'Enter' || event.key === ' ') && renderAsButton) {
      onClick?.(event);
    }
  }

  const shouldAssignAriaControls =
    renderAsButton && showCollapseContent && collapseContent;

  return showSkeleton ? (
    <InviteAccessMemberRowSkeleton ref={ref} />
  ) : (
    <Box ref={ref} paddingBottom={2} position="relative" width="100%" {...rest}>
      <Collapse id={collapseId} isShowing={showCollapseContent}>
        <StyledDivForInviteAccessMemberRow>
          <MemberContainer
            $isHovered={rowIsHovered}
            $useHoverStyles={Boolean(titleOnHover)}
          >
            <StyledButtonForRowClick
              $backgroundColor={backgroundColor}
              aria-controls={shouldAssignAriaControls ? collapseId : undefined}
              aria-label={
                renderAsButton && buttonLabel ? buttonLabel : undefined
              }
              onBlur={handleMouseLeave}
              onClick={onClick}
              onFocus={handleMouseEnter}
              onKeyDown={handleKeyDown}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              role={renderAsButton ? 'button' : undefined}
              tabIndex={renderAsButton ? 0 : undefined}
            >
              <Box
                alignItems="center"
                display="inline-flex"
                flexShrink="0"
                height="100%"
                overflow="hidden"
              >
                {adornmentBefore || (
                  <Avatar
                    alt={title}
                    background={
                      avatarBackground as
                        | ThemeColorsBackground
                        | ThemeColorsMeta
                    }
                    src={avatarSrc}
                  />
                )}
              </Box>
              <StyledDivForRowText>
                <StyledTextForSecondaryTitle
                  color={secondaryTitle ? 'tertiary' : titleColor}
                  enableTooltip={true}
                  truncate={true}
                  variant="body"
                >
                  <Box
                    as="span"
                    color={secondaryTitle ? titleColor : undefined}
                  >
                    {titleOnHover ? (
                      <>
                        <RevealOnHover>{titleOnHover}</RevealOnHover>
                        <HideOnHover $hasTitlePrefix={hasTitlePrefix}>
                          {title}
                        </HideOnHover>
                      </>
                    ) : (
                      <Highlighter
                        autoEscape={true}
                        highlightStyle={{
                          backgroundColor:
                            theme.colors.background.interactive.highlight,
                          color: theme.colors.text.body,
                        }}
                        searchWords={searchWords}
                        textToHighlight={title}
                      />
                    )}
                  </Box>
                  {secondaryTitle || null}
                </StyledTextForSecondaryTitle>
                <Box
                  color="tertiary"
                  display="inline-block"
                  position="relative"
                  width="100%"
                >
                  {metaContent}
                </Box>
              </StyledDivForRowText>
            </StyledButtonForRowClick>

            {enableTooltip && (
              <Box display="flex" paddingRight={8}>
                <TooltipContent
                  {...tooltipResult.bindContent}
                  placement="top"
                  title={tooltipTitle}
                />
                <AnimatedIconContainer
                  {...tooltipResult.bindTarget}
                  animate={{ opacity: 1 }}
                  aria-label={tooltipTitle}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  role="img"
                  tabIndex={0}
                  transition={{
                    delay: 0.1,
                    duration: 0.2,
                    ease: theme.transitions.curves.primary,
                  }}
                >
                  <StyledIconInfo aria-hidden={true} size={24} />
                </AnimatedIconContainer>
              </Box>
            )}
            {adornmentAfter}
          </MemberContainer>
        </StyledDivForInviteAccessMemberRow>

        {collapseContent && (
          <CollapseContent>
            <Box paddingBottom={1} paddingTop={4}>
              {collapseContent}
            </Box>
          </CollapseContent>
        )}
      </Collapse>

      <StyledDivForSeparator
        $hidden={
          (rowIsHovered &&
            !showCollapseContent &&
            !showNonInteractiveHoverState) ||
          !withSeparator
        }
      >
        <Separator color="primary" />
      </StyledDivForSeparator>
    </Box>
  );
};

export const InviteAccessMemberRowSkeleton = forwardRef(
  (_, forwardedRef: Ref<HTMLDivElement>) => (
    <div>
      <Box
        ref={forwardedRef}
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        paddingLeft={4}
        paddingRight={12}
        paddingY={4}
        style={{ height: 40 }}
        width="100%"
      >
        <Box display="inline-flex" height={24} paddingRight={12}>
          <Skeleton delay={1} height={24} rounded={true} width={24} />
        </Box>
        <Box
          alignItems="center"
          display="inline-flex"
          style={{ height: 28 }}
          width="100%"
        >
          <Box
            display="inline-flex"
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
            paddingRight={12}
            width="100%"
          >
            <Skeleton delay={1} height={12} width="75%" />
            <Skeleton delay={1} height={8} width={64} />
          </Box>

          <Box flexShrink="0">
            <Skeleton delay={1} height={24} width="122px" />
          </Box>
        </Box>
      </Box>
      <Box paddingRight={12} style={{ paddingLeft: 40 }}>
        <Separator color="primary" gap={4} />
      </Box>
    </div>
  )
);

export const InviteAccessGroupMemberRowSkeleton = forwardRef(
  (_, forwardedRef: Ref<HTMLDivElement>) => (
    <Box
      ref={forwardedRef}
      alignItems="center"
      display="flex"
      justifyContent="space-between"
      paddingLeft={4}
      paddingRight={12}
      paddingY={4}
      style={{ height: 40 }}
      width="100%"
    >
      <Box display="inline-flex" height={24} paddingRight={12}>
        <Skeleton delay={1} height={24} rounded={true} width={24} />
      </Box>
      <Box
        alignItems="center"
        display="inline-flex"
        style={{ height: 28 }}
        width="100%"
      >
        <Box
          display="inline-flex"
          flexDirection="column"
          height="100%"
          justifyContent="space-between"
          paddingRight={12}
          width="100%"
        >
          <Skeleton delay={1} height={12} width="75%" />
          <Skeleton delay={1} height={8} width={64} />
        </Box>
      </Box>
    </Box>
  )
);

const HideOnHover = styled.span<{ $hasTitlePrefix?: boolean }>`
  ${withOpacityTransition}
  // account for the absolutely positioned prefixed title text space
  padding-left: ${(p) => (p.$hasTitlePrefix ? '30px' : 'initial')};
`;

const StyledDivForInviteAccessMemberRow = styled.div`
  align-items: center;
  border-radius: ${theme.radii[6]};
  display: flex;
  height: ${theme.spaces[48]};
  justify-content: space-between;
  /* HACK: This fixes a render issue with false textures bleeding past their bounds after a row is hovered. */
  max-width: 99.9%;
  position: relative;
  user-select: none;
  width: 100%;

  ${whenHovered} {
    background-color: ${theme.colors.background.tertiary};
  }

  &:focus-within {
    background-color: ${theme.colors.background.tertiary};
  }

  ${HideOnHover} {
    padding-left: initial;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: auto;
  }
`;

const StyledButtonForRowClick = styled.div<{
  $backgroundColor?: InviteAccessMemberProps['backgroundColor'];
}>`
  background: none;
  border: none;
  border-radius: ${theme.radii[6]};
  display: flex;
  height: 100%;
  outline: none;
  overflow: hidden;
  padding: 0;
  width: 100%;

  @media (max-width: ${theme.breakpoints.tablet}) {
    overflow: visible;
  }

  &[role='button'] {
    cursor: pointer;
  }

  &[disabled] {
    cursor: default;
  }

  ${whenFocused} {
    box-shadow: 0 0 0 1px ${theme.colors.border.interactive.focused};
  }
`;

const StyledDivForSeparator = styled.div<{ $hidden: boolean }>`
  opacity: ${(p) => (p.$hidden ? 0 : 1)};
  /* 44px is the distance between the first item in the row and where
   * the border should start with  no adornmentBefore, and 88px with adornmentBefore
   */
  padding: 0 ${theme.spaces[8]} 0 44px;
  position: relative;
  ${withOpacityTransition};
`;

const RevealOnHover = styled.div`
  ${withOpacityTransition}
  color: ${theme.colors.text.body};
  width: 100%;
  z-index: ${theme.zIndices.base + 1};
`;

const MemberContainer = styled.div<{
  $isHovered?: boolean;
  $useHoverStyles?: boolean;
}>`
  ${withBackgroundTransition}
  align-items: center;
  cursor: default;
  display: flex;
  height: 100%;
  justify-content: space-between;
  padding: ${theme.spaces[4]} ${theme.spaces[8]};
  width: 100%;

  @media (hover: none) {
    ${HideOnHover} {
      display: none;
    }
  }

  /** Only apply these styles for devices that have a pointer device that can hover */
  @media (hover: hover) {
    ${HideOnHover} {
      opacity: ${(p) => (p.$useHoverStyles && !p.$isHovered ? 1 : 0)};
    }

    ${RevealOnHover} {
      opacity: ${(p) => (p.$useHoverStyles && !p.$isHovered ? 0 : 1)};
    }
  }
`;

const StyledTextForSecondaryTitle = styled(Text)`
  overflow-wrap: break-word;

  ${whenHovered} {
    text-align: center;
  }
`;

const StyledDivForRowText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  /**
   * Zeroing out the line height keeps the vertical space between the
   * Primary content and the meta content consistent across browsers
   */
  line-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 0 ${theme.spaces[16]} 0 ${theme.spaces[12]};
  text-align: left;
  width: 100%;

  ${StyledTextForSecondaryTitle} {
    text-align: left;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    overflow: visible;

    && * {
      white-space: normal;
      word-break: break-word;
      text-align: left;
    }
  }
`;

const StyledIconInfo = styled(IconInfo)`
  color: ${theme.colors.icon.interactive.off};
  transition: color 0.1s ${theme.transitions.eases.primary};

  ${whenHovered} {
    color: ${theme.colors.icon.interactive.hover};
  }
`;

const AnimatedIconContainer = styled(motion.div)`
  align-items: center;
  display: inline-flex;
`;
